import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SEOHead, getWebApplicationStructuredData, getOrganizationStructuredData } from "../components/SEOHelpers";
import "./Home.css";

const Home = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Efeito para animação de entrada
  useEffect(() => {
    setIsVisible(true);
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.body.classList.toggle("dark-mode", !isDarkMode);
  };

  // Criar dados estruturados de forma segura
  const createStructuredData = () => {
    try {
      const organizationData = getOrganizationStructuredData();
      const webAppData = getWebApplicationStructuredData();

      // Verificar se os dados são válidos
      if (!organizationData || !webAppData) {
        console.warn('Structured data functions returned invalid data');
        return null;
      }

      return {
        "@context": "https://schema.org",
        "@graph": [
          organizationData,
          webAppData,
          {
            "@type": "WebPage",
            "@id": "https://casal-resolve.vercel.app/#webpage",
            "url": "https://casal-resolve.vercel.app/",
            "name": "Casal Resolve - Plataforma de Resolução de Conflitos para Casais",
            "isPartOf": {
              "@id": "https://casal-resolve.vercel.app/#website"
            },
            "description": "Transforme conflitos em oportunidades de crescimento. Plataforma com IA para ajudar casais a melhorarem comunicação e resolverem conflitos.",
            "breadcrumb": {
              "@id": "https://casal-resolve.vercel.app/#breadcrumb"
            }
          }
        ]
      };
    } catch (error) {
      console.error('Error creating structured data:', error);
      return null;
    }
  };

  // Memoizar structured data para evitar recriação desnecessária
  const structuredData = React.useMemo(() => createStructuredData(), []);

  try {
    return (
      <>
        <SEOHead
          title="Transforme Conflitos em Crescimento"
          description="Plataforma de resolução de conflitos para casais com IA. Melhore comunicação, resolva conflitos e fortaleça seu relacionamento com ferramentas práticas e eficazes."
          keywords="relacionamento, casal, conflitos, resolução, comunicação, terapia de casal, relacionamento saudável, crescimento pessoal, inteligência artificial"
          url="https://casal-resolve.vercel.app/"
          type="website"
          structuredData={structuredData}
        />

        <div className={`home-container ${isDarkMode ? "dark" : ""} ${isVisible ? "visible" : ""}`}>
          <header className="home-header">
            <div className="logo">
              <span className="logo-icon">❤️</span>
              <span className="logo-text">Casal Resolve</span>
            </div>
            <button onClick={toggleDarkMode} className="toggle-button" aria-label={isDarkMode ? "Ativar modo claro" : "Ativar modo escuro"}>
              {isDarkMode ? "☀️" : "🌙"}
            </button>
          </header>

          <main className="home-main">
            <section className="hero-section">
              <div className="hero-content">
                <h1 className="animated-title">Transforme seus conflitos em oportunidades de crescimento</h1>
                <p className="hero-description">
                  O <strong>Casal Resolve</strong> é uma plataforma que ajuda casais a melhorarem sua comunicação,
                  resolverem conflitos e fortalecerem seu relacionamento através de ferramentas práticas e eficazes.
                </p>
                <div className="action-links">
                  <Link to="/login" className="btn-primary">Entrar</Link>
                  <Link to="/register" className="btn-secondary">Cadastrar</Link>
                </div>
              </div>
              <div className="hero-image">
                <div className="illustration"></div>
              </div>
            </section>

            <section className="features-section">
              <h2>Como podemos ajudar seu relacionamento</h2>
              <div className="features-grid">
                <div className="feature-card">
                  <div className="feature-icon" aria-hidden="true">💬</div>
                  <h3>Comunicação Eficaz</h3>
                  <p>Aprenda técnicas para expressar sentimentos e necessidades de forma clara e respeitosa.</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon" aria-hidden="true">🤝</div>
                  <h3>Resolução de Conflitos</h3>
                  <p>Transforme discussões em conversas produtivas que levam a soluções satisfatórias para ambos.</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon" aria-hidden="true">🌱</div>
                  <h3>Crescimento Conjunto</h3>
                  <p>Fortaleça seu vínculo e cresçam juntos através de cada desafio superado.</p>
                </div>
              </div>
            </section>

            <section className="how-it-works-section">
              <h2>Como funciona</h2>
              <div className="steps-container">
                <div className="step">
                  <div className="step-number" aria-hidden="true">1</div>
                  <h3>Registre o conflito</h3>
                  <p>Descreva a situação que está gerando tensão no relacionamento.</p>
                </div>
                <div className="step">
                  <div className="step-number" aria-hidden="true">2</div>
                  <h3>Siga o processo guiado</h3>
                  <p>Nossa plataforma oferece um passo a passo estruturado para resolver o conflito.</p>
                </div>
                <div className="step">
                  <div className="step-number" aria-hidden="true">3</div>
                  <h3>Encontre soluções juntos</h3>
                  <p>Trabalhem em conjunto para chegar a um acordo que satisfaça ambas as partes.</p>
                </div>
                <div className="step">
                  <div className="step-number" aria-hidden="true">4</div>
                  <h3>Fortaleça seu relacionamento</h3>
                  <p>Cada conflito resolvido é uma oportunidade para crescerem como casal.</p>
                </div>
              </div>
            </section>

            <section className="testimonials-section">
              <h2>O que nossos usuários dizem</h2>
              <div className="testimonial-card">
                <blockquote>
                  <p>"O Casal Resolve transformou nosso relacionamento. Aprendemos a conversar de forma mais saudável e a resolver nossos problemas juntos."</p>
                  <footer className="testimonial-author">
                    <cite>Maria e João, juntos há 5 anos</cite>
                  </footer>
                </blockquote>
              </div>
            </section>

            <section className="cta-section">
              <div className="cta-content">
                <h2>Comece a transformar seu relacionamento hoje</h2>
                <p>Junte-se a milhares de casais que estão fortalecendo seus relacionamentos através da comunicação eficaz e resolução de conflitos.</p>
                <Link to="/register" className="cta-button">Criar conta gratuita</Link>
              </div>
            </section>
          </main>

          <footer className="home-footer">
            <div className="footer-content">
              <div className="footer-logo">
                <span className="logo-icon" aria-hidden="true">❤️</span>
                <span className="logo-text">Casal Resolve</span>
              </div>
              <nav className="footer-links" aria-label="Links do rodapé">
                <a href="/sobre">Sobre nós</a>
                <a href="/termos">Termos de uso</a>
                <a href="/privacidade">Política de privacidade</a>
                <a href="/contato">Contato</a>
              </nav>
            </div>
            <p className="copyright">&copy; 2025 Casal Resolve - Plataforma para resolução de conflitos</p>
          </footer>
        </div>
      </>
    );
  } catch (error) {
    console.error('Home component render error:', error);
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>Erro na página inicial</h1>
        <p>Ocorreu um erro ao carregar a página. Tente recarregar.</p>
        <button onClick={() => window.location.reload()}>
          Recarregar Página
        </button>
      </div>
    );
  }
};

export default Home;
